import { useState, useRef, useEffect, useMemo } from 'react';
import { MapPin, ChevronsUpDown, Check, X, Search } from 'lucide-react';
import type { Location } from '../types';
import { getRecentLocationIds, addRecentLocationId } from '../utils/preferences';

interface LocationComboboxProps {
  locations: Location[];
  selectedId: string;
  onChange: (locationId: string) => void;
  id?: string;
  label?: string;
  disabled?: boolean;
  maxPills?: number;
}

const COMMON_NAMES = ['office', 'office-be', 'home office', 'client site', 'remote'];

export function LocationCombobox({
  locations,
  selectedId,
  onChange,
  id = 'entry-location',
  label = 'Location',
  disabled = false,
  maxPills = 4
}: LocationComboboxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [recentLocationIds, setRecentLocationIds] = useState<string[]>(() => getRecentLocationIds());

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Sync recent locations from localStorage when selectedId changes
  const [prevSelectedId, setPrevSelectedId] = useState(selectedId);
  if (selectedId !== prevSelectedId) {
    setPrevSelectedId(selectedId);
    setRecentLocationIds(getRecentLocationIds());
  }

  // Identify currently selected location object
  const selectedLocation = useMemo(() => {
    return locations.find(l => l.id === selectedId);
  }, [locations, selectedId]);

  // Common quick-pick locations
  const defaultQuickPickLocations = useMemo(() => {
    return locations.filter(loc =>
      COMMON_NAMES.some(c => loc.name.toLowerCase() === c || loc.name.toLowerCase().startsWith('office'))
    );
  }, [locations]);

  // Recent locations from localStorage
  const recentLocations = useMemo(() => {
    return recentLocationIds
      .map(id => locations.find(l => l.id === id))
      .filter((loc): loc is Location => Boolean(loc));
  }, [locations, recentLocationIds]);

  // Combined quick-pick locations (default + recent from localStorage, deduplicated, max 8)
  const quickPickLocations = useMemo(() => {
    const combined: Location[] = [];
    const seenIds = new Set<string>();

    // Add default locations first
    defaultQuickPickLocations.forEach(loc => {
      if (!seenIds.has(loc.id)) {
        seenIds.add(loc.id);
        combined.push(loc);
      }
    });

    // Add recent locations from localStorage
    recentLocations.forEach(loc => {
      if (!seenIds.has(loc.id)) {
        seenIds.add(loc.id);
        combined.push(loc);
      }
    });

    return combined.slice(0, maxPills);
  }, [defaultQuickPickLocations, recentLocations, maxPills]);

  // Filter locations based on query
  const filteredLocations = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return locations;
    return locations.filter(l => l.name.toLowerCase().includes(q));
  }, [locations, searchQuery]);

  // Reset highlighted index when search query changes
  const [prevSearchQuery, setPrevSearchQuery] = useState(searchQuery);
  if (searchQuery !== prevSearchQuery) {
    setPrevSearchQuery(searchQuery);
    setHighlightedIndex(0);
  }

  // Scroll highlighted item into view
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    const activeItem = listRef.current.children[highlightedIndex] as HTMLElement;
    if (activeItem && typeof activeItem.scrollIntoView === 'function') {
      activeItem.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex, isOpen]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (locId: string) => {
    onChange(locId);
    setIsOpen(false);
    setSearchQuery('');
    const updated = addRecentLocationId(locId);
    setRecentLocationIds(updated);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev < filteredLocations.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredLocations.length > 0 && filteredLocations[highlightedIndex]) {
        handleSelect(filteredLocations[highlightedIndex].id);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  // Render max 60 items at a time for optimal performance
  const displayLimit = 60;
  const visibleItems = filteredLocations.slice(0, displayLimit);

  return (
    <div ref={containerRef} className="space-y-1.5 relative">
      <div className="flex items-center justify-between">
        <label htmlFor={id} className="block text-sm font-medium text-slate-700">
          {label}
        </label>
        {locations.length > 0 && (
          <span className="text-xs text-slate-400">
            {searchQuery.trim()
              ? `${filteredLocations.length} match${filteredLocations.length === 1 ? '' : 'es'}`
              : `${locations.length} locations`}
          </span>
        )}
      </div>

      {/* Quick-pick buttons for common locations */}
      {quickPickLocations.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-0.5">
          {quickPickLocations.map(loc => {
            const isSelected = selectedId === loc.id;
            return (
              <button
                key={loc.id}
                type="button"
                onClick={() => handleSelect(loc.id)}
                disabled={disabled}
                className={`text-xs px-2.5 py-1 rounded-md font-medium transition-all cursor-pointer border ${
                  isSelected
                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                    : 'bg-slate-100 hover:bg-slate-200 text-slate-600 border-slate-200/80 hover:text-slate-800'
                }`}
              >
                {loc.name}
              </button>
            );
          })}
        </div>
      )}

      {/* Unified Searchable Combobox Input */}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 flex items-center">
          {isOpen ? <Search className="w-4 h-4 text-indigo-500" /> : <MapPin className="w-4 h-4 text-slate-400" />}
        </div>

        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          data-testid="location-combobox-input"
          aria-label="Search location"
          placeholder={selectedLocation ? selectedLocation.name : 'Search or select location...'}
          value={isOpen ? searchQuery : (selectedLocation?.name || '')}
          onFocus={() => {
            setIsOpen(true);
            setSearchQuery('');
          }}
          onChange={e => {
            setSearchQuery(e.target.value);
            if (!isOpen) setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          className="w-full pl-9 pr-16 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all shadow-sm bg-white cursor-pointer text-slate-800 text-sm font-medium"
        />

        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isOpen && searchQuery && (
            <button
              type="button"
              onClick={e => {
                e.stopPropagation();
                setSearchQuery('');
                inputRef.current?.focus();
              }}
              className="p-1 text-slate-400 hover:text-slate-600 rounded-md cursor-pointer"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (isOpen) {
                setIsOpen(false);
                setSearchQuery('');
              } else {
                setIsOpen(true);
                inputRef.current?.focus();
              }
            }}
            tabIndex={-1}
            className="p-1 text-slate-400 hover:text-slate-600 rounded-md cursor-pointer"
          >
            <ChevronsUpDown className="w-4 h-4" />
          </button>
        </div>

        {/* Underlying native select element for screen readers & Playwright/Vitest compatibility */}
        <select
          id={id}
          value={selectedId}
          onChange={e => handleSelect(e.target.value)}
          disabled={disabled}
          tabIndex={-1}
          className="absolute inset-0 opacity-0 pointer-events-none w-full h-full -z-10"
        >
          <option value="" disabled>Select location</option>
          {locations.map(l => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
      </div>

      {/* Dropdown Menu Popover */}
      {isOpen && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white rounded-xl border border-slate-200 shadow-xl overflow-hidden animate-in fade-in zoom-in-95 duration-100">
          <ul
            ref={listRef}
            className="max-h-60 overflow-y-auto py-1 text-sm divide-y divide-slate-50 focus:outline-none"
            role="listbox"
          >
            {visibleItems.length === 0 ? (
              <li className="px-4 py-3 text-sm text-slate-400 text-center">
                No locations match "{searchQuery}"
              </li>
            ) : (
              visibleItems.map((loc, idx) => {
                const isSelected = loc.id === selectedId;
                const isHighlighted = idx === highlightedIndex;

                return (
                  <li
                    key={loc.id}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => handleSelect(loc.id)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-3.5 py-2.5 flex items-center justify-between cursor-pointer transition-colors ${
                      isHighlighted ? 'bg-indigo-50 text-indigo-900 font-medium' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    <span className="truncate">
                      {loc.name}
                    </span>
                    {isSelected && (
                      <Check className="w-4 h-4 text-indigo-600 shrink-0 ml-2" />
                    )}
                  </li>
                );
              })
            )}
          </ul>

          {filteredLocations.length > displayLimit && (
            <div className="px-3 py-1.5 bg-slate-50 text-xs text-slate-400 border-t border-slate-100 text-center">
              Showing top {displayLimit} of {filteredLocations.length} matches — type to narrow down
            </div>
          )}
        </div>
      )}
    </div>
  );
}
